import React from "react";
import { CreditCard, Info } from "lucide-react";
import { planLabel } from "../../actions/billing";
import { describeCard } from "./PaymentMethodCard";

// Decorative only: two stacked cards behind a verified shield, echoing the
// brand colours rather than shipping a raster asset.
const CardsArt = () => (
  <svg
    className="bill-pm-art"
    viewBox="0 0 200 130"
    aria-hidden="true"
    focusable="false"
  >
    <defs>
      <linearGradient id="bill-pm-back" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#8b8ff5" />
        <stop offset="100%" stopColor="#6366f1" />
      </linearGradient>
      <linearGradient id="bill-pm-front" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#4f46e5" />
        <stop offset="100%" stopColor="#3f44e8" />
      </linearGradient>
      <linearGradient id="bill-pm-shield" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#a5b4fc" />
        <stop offset="100%" stopColor="#6d76f7" />
      </linearGradient>
    </defs>

    <rect x="16" y="46" width="104" height="64" rx="12" fill="url(#bill-pm-back)" opacity="0.85" />
    <rect x="14" y="42" width="18" height="12" rx="3" fill="#ffffff" opacity="0.35" />

    <rect x="62" y="18" width="112" height="70" rx="13" fill="url(#bill-pm-front)" />
    <rect x="76" y="38" width="22" height="15" rx="4" fill="#ffffff" opacity="0.45" />
    <rect x="76" y="62" width="62" height="7" rx="3.5" fill="#ffffff" opacity="0.3" />

    <path
      d="M140 62l26 9v20c0 14-11 22-26 27-15-5-26-13-26-27V71l26-9z"
      fill="url(#bill-pm-shield)"
    />
    <path
      d="M130 96l7 7 14-15"
      fill="none"
      stroke="#ffffff"
      strokeWidth="5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * The Payment methods tab.
 *
 * The card a subscription renews on. `helcimSubscriptionId` on the record,
 * or the id on `paymentMethod`, is what decides between the connected and
 * empty states.
 */
export default function PaymentMethodsPanel({
  subscription,
  loading = false,
  onChangePlan,
}) {
  const method = subscription?.paymentMethod;
  const subscriptionId =
    subscription?.helcimSubscriptionId || method?.subscriptionId;
  // "Card ending 9990 · expires 01/28", when the record carries the card.
  const card = describeCard(method?.card);
  const status = subscription?.status;
  const plan = subscription?.plan;
  const isActive = String(status || "").toLowerCase() === "active";

  return (
    <div className="bill-pm">
      <header className="bill-pm-hero">
        <div className="bill-pm-hero__text">
          <h2 className="bill-pm-hero__title">Payment methods</h2>
          <p className="bill-pm-hero__sub">
            Manage how you pay for your subscription. Keep your payment methods
            secure and up to date.
          </p>
        </div>
        <CardsArt />
      </header>

      <section className="dash-card bill-pm-card">
        <div className="bill-pm-card__head">
          <div>
            <h3 className="bill-pm-card__title">Your payment methods</h3>
            <p className="bill-pm-card__sub">
              These payment methods are used for your active subscriptions.
            </p>
          </div>


        </div>

        {loading ? (
          <div className="bill-pm-row">
            <span className="skel bill-pm-row__logo" />
            <div className="bill-pm-row__text">
              <span className="skel acct-skel--label" />
              <span className="skel acct-skel--desc" />
            </div>
          </div>
        ) : subscriptionId ? (
          <div className="bill-pm-row">
            <span className="bill-pm-row__logo">
              <CreditCard size={24} strokeWidth={1.8} />
            </span>

            <div className="bill-pm-row__text">
              <p className="bill-pm-row__name">
                {card || "Card on file"}
                {(method?.statusLabel || status) && (
                  <span
                    className={`bill-pm-status${isActive ? " is-active" : " is-pending"}`}
                  >
                    {method?.statusLabel || planLabel(status)}
                  </span>
                )}
              </p>
              {plan && (
                <span className="bill-pm-row__meta">
                  Billed for the {planLabel(plan)} plan
                </span>
              )}
              {method?.card?.holderName && (
                <span className="bill-pm-row__meta">
                  {method.card.holderName}
                </span>
              )}
            </div>
          </div>
        ) : (
          <div className="bill-pm-row">
            <span className="bill-pm-row__logo">
              <CreditCard size={24} strokeWidth={1.8} />
            </span>
            <div className="bill-pm-row__text">
              <p className="bill-pm-row__name">No payment method yet</p>
              <span className="bill-pm-row__meta">
                Your card is taken when you move to a paid plan, and kept for
                renewals.
              </span>
            </div>
            {onChangePlan && (
              <button
                type="button"
                className="onboarding__btn onboarding__btn--primary bill-pm-choose"
                onClick={onChangePlan}
              >
                Choose a plan
              </button>
            )}
          </div>
        )}

        <p className="bill-pm-note">
          <span className="bill-pm-note__icon">
            <Info size={17} strokeWidth={2} />
          </span>
          {method?.cancelNote ||
            "Renewals are charged to this card. Cancelling stops all future payments; your plan keeps working until the end of the period you have already paid for."}
        </p>
      </section>
    </div>
  );
}
